import Link from 'next/link';
import { format } from 'date-fns';
import Image from 'next/image';

import { H6 } from '@common/headline';
import { BodyRegular } from '@common/body';

import { getAuthorURL } from '@nextUtils/guidesUtils';

import facebook from '@public/img/logo/facebook.png';
import twitter from '@public/img/logo/twitter.png';

type Props = {
  absoluteCanonicalURL: string;
  authorImageUrl: string;
  authorName: string;
  datePublished: string;
  dateModified?: Date;
  isBlog: boolean;
  isStory: boolean;
  readingTime: number;
  showDatePublished: boolean;
};

const Sidebar = ({
  absoluteCanonicalURL,
  authorImageUrl,
  authorName,
  datePublished,
  dateModified,
  isBlog,
  isStory,
  readingTime,
  showDatePublished,
}: Props): JSX.Element => (
  <div className="flex flex-col text-blue">
    {!isStory && (
      <>
        <Link href={getAuthorURL(authorName)}>
          <Image
            className="mb-3 rounded-[26px]"
            src={authorImageUrl.replace('s=96', 's=120')}
            alt={authorName}
            width={52}
            height={52}
            priority
          />
        </Link>
        <H6>Written by</H6>
        <Link
          href={getAuthorURL(authorName)}
          className="mb-5 uppercase text-blue-600 hover:text-blue"
        >
          <BodyRegular>{authorName}</BodyRegular>
          {authorName === 'Robbie Puddick (RNutr)' && (
            <BodyRegular>Content and SEO Lead</BodyRegular>
          )}
        </Link>
        {!isBlog && (
          <>
            <H6>
              {absoluteCanonicalURL.includes('/recipes/')
                ? 'Reviewed by dietitian'
                : 'Medically reviewed by'}
            </H6>
            <Link
              href={getAuthorURL('Rachel')}
              className="mb-5 uppercase text-blue-600 hover:text-blue"
            >
              <BodyRegular>Dr Rachel Hall (MBCHB)</BodyRegular>
              <BodyRegular>Principal Doctor</BodyRegular>
            </Link>
          </>
        )}
        {dateModified ? (
          <div className="text-blue-400">
            <H6>{readingTime} min read</H6>
            <H6>
              {showDatePublished
                ? `Published on ${format(new Date(datePublished), 'do MMMM yyyy')}`
                : `Last updated ${format(dateModified, 'MMMM yyyy')}`}
            </H6>
          </div>
        ) : (
          <div className="text-blue-400">
            <H6>{format(new Date(datePublished), 'do MMMM yyyy')}</H6>
          </div>
        )}
      </>
    )}
    <div className={!isStory ? 'mt-4' : ''}>
      <H6>Share</H6>
      <div className="mb-6 mt-2 flex gap-2">
        <a
          href={`https://www.facebook.com/sharer/sharer.php?u=${absoluteCanonicalURL}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Image src={facebook} alt="Facebook" width={40} height={40} />
        </a>
        <a
          href={`https://twitter.com/share?url=${absoluteCanonicalURL}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Image src={twitter} alt="Twitter" width={40} height={40} />
        </a>
      </div>
    </div>
  </div>
);

export default Sidebar;
